import { useEffect, useState } from 'react';
import { Clock, FileText, BarChart3 } from 'lucide-react';
import { api, buildQuery } from '../../lib/api';
import type { Report, ReportSummary } from '../../types';
import { formatDate, formatDateTime } from '../../lib/format';
import { Spinner, EmptyState, StatusBadge, Avatar } from '../../components/ui';
import ReportDetailModal from '../../components/ReportDetailModal';
import { ALL_REPORT_TYPES, REPORT_TYPE_LABELS } from '../../components/ReportFormModal';

export default function Reports() {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [selected, setSelected] = useState<Report | null>(null);

  useEffect(() => {
    setLoading(true);
    api
      .get<Report[]>(`/reports${buildQuery({ report_type: typeFilter })}`)
      .then(setReports)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [typeFilter]);

  useEffect(() => {
    api
      .get<ReportSummary>('/reports/summary')
      .then(setSummary)
      .catch(() => {});
  }, []);

  const openReport = (r: Report) => {
    setSelected(r);
    api
      .get<Report>(`/reports/${r.id}`)
      .then((fresh) => setSelected(fresh))
      .catch(() => {});
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Reports</h1>

      {summary && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center space-x-2 mb-4">
            <BarChart3 className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Automatic Summary</h2>
          </div>
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-primary/10 text-primary-dark text-sm">
              <FileText className="h-4 w-4" />
              <span>{summary.total} report(s)</span>
            </div>
            {summary.by_type.map((t) => (
              <span key={t.report_type} className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-sm capitalize">
                {t.report_type.replace(/_/g, ' ')}: {t.count}
              </span>
            ))}
          </div>
          <p className="text-sm text-gray-700 whitespace-pre-line border-t border-gray-100 pt-4">{summary.summary}</p>
        </div>
      )}

      <div className="mb-4">
        <select
          className="w-48 px-4 py-2 border border-gray-300 rounded-lg text-sm"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="">All types</option>
          {ALL_REPORT_TYPES.map((t) => (
            <option key={t} value={t}>{REPORT_TYPE_LABELS[t]}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <Spinner label="Loading reports..." />
      ) : reports.length === 0 ? (
        <EmptyState message="No reports found." />
      ) : (
        <div className="grid gap-4">
          {reports.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => openReport(r)}
              className="bg-white p-5 rounded-lg shadow-lg text-left hover:shadow-xl hover:ring-1 hover:ring-primary/40 transition"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                <div className="flex items-center">
                  <Avatar src={r.image_url} name={r.user_name} className="w-8 h-8 mr-3" />
                  <div>
                    <p className="font-medium">{r.user_name}</p>
                    <p className="text-xs text-gray-500 capitalize">{r.user_role}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <StatusBadge status={r.report_type} />
                  {r.facility && (
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{r.facility}</span>
                  )}
                  {r.estate && (
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{r.estate}</span>
                  )}
                  {r.category && r.category !== 'general' && (
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 capitalize">{r.category}</span>
                  )}
                  <span className="text-sm text-gray-500">{formatDate(r.report_date)}</span>
                </div>
              </div>
              {r.heading && <h3 className="font-semibold text-gray-800 mb-1">{r.heading}</h3>}
              <p className="text-sm text-gray-600 whitespace-pre-line mb-2 line-clamp-3">{r.content}</p>
              <div className="flex items-center text-xs text-gray-400">
                <Clock className="h-3.5 w-3.5 mr-1" />
                Uploaded {formatDateTime(r.created_at)} · Click to open
              </div>
            </button>
          ))}
        </div>
      )}

      <ReportDetailModal
        report={selected}
        onClose={() => setSelected(null)}
        onAttachmentDeleted={(attId) => {
          setSelected((cur) =>
            cur ? { ...cur, attachments: cur.attachments.filter((a) => a.id !== attId) } : cur
          );
          setReports((prev) =>
            prev.map((r) =>
              r.id === selected?.id ? { ...r, attachments: r.attachments.filter((a) => a.id !== attId) } : r
            )
          );
        }}
      />
    </div>
  );
}
